import { SetStateAction, useCallback, useMemo } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import type {
  SavedSearchGroup,
  SavedSearchItem,
} from 'talent_search/shared/types';
import type { SavedFiltersProps } from 'talent_matching/components/filters/saved_filters';
import type { SavedSearchPaths } from 'talent_matching/api';
import {
  fetchSavedSearch as apiFetchSavedSearch,
  createSavedSearch as apiCreateSavedSearch,
  updateSavedSearch as apiUpdateSavedSearch,
} from 'talent_matching/api';
import { parseFormFieldsFromSavedSearch } from 'talent_matching/components/filters/utils';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.saved_filters');

type UseSavedFiltersConfig = {
  formDefaults: Record<string, unknown>;
  savedFilterSet: SavedSearchItem | null;
  setSavedFilterSet: (item: SavedSearchItem | null) => void;
  buildQueryString?: () => string;
  paths: SavedSearchPaths;
  savedSearchOptions: Item[];
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  parseSavedSearch?: (response: unknown) => Record<string, unknown>;
  featureType?: string;
  presetGroups?: SavedSearchGroup[];
};

type UseSavedFiltersReturn = {
  savedFiltersProps: SavedFiltersProps;
  handleClearAll: () => void;
};

export function useSavedFilters(
  config: UseSavedFiltersConfig
): UseSavedFiltersReturn {
  const { reset } = useFormContext();
  const {
    formDefaults,
    savedFilterSet,
    setSavedFilterSet,
    paths,
    savedSearchOptions,
    setSavedSearchOptions,
    featureType,
    presetGroups,
  } = config;
  const parseFn = config.parseSavedSearch ?? parseFormFieldsFromSavedSearch;

  const handleClearAll = useCallback(() => {
    setSavedFilterSet(null);
    reset(formDefaults);
  }, [setSavedFilterSet, reset, formDefaults]);

  const loadOptions = useMemo((): SavedSearchGroup[] => {
    const userGroup: SavedSearchGroup = {
      text: t('your_filters'),
      items: savedSearchOptions.map((o) => ({
        id: Number(o.value),
        label: o.label ?? '',
      })),
    };
    return [userGroup, ...(presetGroups ?? [])];
  }, [savedSearchOptions, presetGroups]);

  const fetchAndParseSavedSearch = useCallback(
    async (option: SavedSearchItem) => {
      if (option.id === undefined) {
        const preset = option.value as
          | { value: Record<string, unknown> }
          | undefined;
        const raw = preset?.value ?? {};
        return parseFn(raw);
      }
      const response = await apiFetchSavedSearch(paths, option.id, featureType);
      return parseFn(response);
    },
    [paths, featureType, parseFn]
  );

  const createSavedSearch = useCallback(
    async (name: string, queryString: string) => {
      const response = await apiCreateSavedSearch(
        paths,
        queryString,
        name,
        featureType
      );
      const newOption = {
        value: response.id.toString(),
        label: response.label,
      };
      setSavedSearchOptions((prev: Item[]) => [newOption, ...prev]);
      return { id: response.id, label: response.label };
    },
    [paths, featureType, setSavedSearchOptions]
  );

  const updateSavedSearch = useCallback(
    async (queryString: string) => {
      if (!savedFilterSet?.id) {
        return;
      }
      await apiUpdateSavedSearch(
        paths,
        savedFilterSet.id,
        queryString,
        savedFilterSet.label,
        featureType
      );
    },
    [paths, featureType, savedFilterSet]
  );

  return {
    savedFiltersProps: {
      savedFilterSet,
      setSavedFilterSet,
      formDefaults,
      loadOptions,
      fetchAndParseSavedSearch,
      createSavedSearch,
      updateSavedSearch,
      buildQueryString: config.buildQueryString,
    },
    handleClearAll,
  };
}
